const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i;
const HEX_ID_PATTERN = /^[0-9a-f]{16,}$/i;
const NUMERIC_PATTERN = /^\d+$/;
const SAFE_TOKEN_PATTERN = /^[a-zA-Z0-9_-]+$/;

const MIXED_ALNUM_MIN_LENGTH = 5;
const LONG_TOKEN_MIN_LENGTH = 20;

function hasDigit(segment: string): boolean {
  return /\d/.test(segment);
}

function hasLetter(segment: string): boolean {
  return /[a-zA-Z]/.test(segment);
}

/**
 * Decides whether a single path segment looks like a dynamic identifier
 * rather than a static route name. Static words (users, profile, orders,
 * env, ...) are left untouched; numeric ids, UUIDs, Mongo ObjectIds, hex
 * ids, and mixed alphanumeric slugs are replaced with `:id`.
 */
export function isDynamicSegment(segment: string): boolean {
  if (segment.length === 0) return false;
  if (NUMERIC_PATTERN.test(segment)) return true;
  if (UUID_PATTERN.test(segment)) return true;
  if (OBJECT_ID_PATTERN.test(segment)) return true;
  if (HEX_ID_PATTERN.test(segment)) return true;

  if (!SAFE_TOKEN_PATTERN.test(segment)) {
    // Contains characters outside the usual id alphabet (e.g. dots from a
    // filename extension embedded in the path) — only treat as dynamic if
    // it clearly mixes letters and digits and is reasonably long.
    return hasDigit(segment) && hasLetter(segment) && segment.length >= MIXED_ALNUM_MIN_LENGTH;
  }

  if (segment.length >= LONG_TOKEN_MIN_LENGTH) return true;

  if (hasDigit(segment) && hasLetter(segment) && segment.length >= MIXED_ALNUM_MIN_LENGTH) {
    return true;
  }

  return false;
}

/** Strip query string and fragment; keep the real path for filter UIs. */
export function routePathOnly(route: string): string {
  if (typeof route !== "string" || route.trim() === "") {
    return route ?? "";
  }
  const withoutFragment = route.split("#")[0] ?? route;
  return withoutFragment.split("?")[0] ?? withoutFragment;
}

/**
 * Strips query string and fragment, then replaces dynamic-looking path
 * segments with `:id`. Never mutates or returns the original route string
 * for storage — callers keep the original `route` alongside this value.
 */
export function normalizeRoute(route: string): string {
  if (typeof route !== "string" || route.trim() === "") {
    return route ?? "";
  }

  const withoutQuery = routePathOnly(route);

  const segments = withoutQuery.split("/");
  const normalizedSegments = segments.map((segment) =>
    isDynamicSegment(segment) ? ":id" : segment,
  );

  return normalizedSegments.join("/");
}

export type RouteGroupingField = "route" | "normalizedRoute";

export function getRouteGroupingField(
  routeGroupingEnabled: boolean | undefined,
): RouteGroupingField {
  return routeGroupingEnabled === false ? "route" : "normalizedRoute";
}
